import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, gbp, fmtDate, fileUrl, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, Download, Trash2, Pencil, BookOpen } from "lucide-react";
import { toast } from "sonner";

export default function PurchaseOrders() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/purchase-orders").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = items.filter((it) => {
    const s = q.toLowerCase();
    return (
      !s ||
      it.number.toLowerCase().includes(s) ||
      (it.reference || "").toLowerCase().includes(s) ||
      (it.customer_snapshot?.name || "").toLowerCase().includes(s)
    );
  });

  const onDelete = async (po) => {
    if (!window.confirm(`Delete ${po.number}?`)) return;
    try {
      await api.delete(`/purchase-orders/${po.id}`);
      toast.success(`${po.number} deleted`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="po-list-page" className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Documents</div>
          <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900 mt-1">
            Purchase Orders
          </h1>
          <p className="text-slate-600 mt-2 text-sm">
            Create and manage POs issued to your subcontractors.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/purchase-orders/register">
            <Button data-testid="po-register-btn" variant="outline" className="rounded-sm border-slate-300">
              <BookOpen className="h-4 w-4 mr-2" /> View Register
            </Button>
          </Link>
          <Link to="/purchase-orders/new">
            <Button data-testid="po-new-btn" className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white">
              <Plus className="h-4 w-4 mr-2" /> New Purchase Order
            </Button>
          </Link>
        </div>
      </div>

      <Card className="rounded-sm border border-slate-200 shadow-none">
        <div className="p-4 border-b border-slate-200 flex items-center gap-3">
          <div className="relative w-full max-w-sm">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="po-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search PO number or subcontractor…"
              className="pl-9 rounded-sm border-slate-300"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full po-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider">
                <th className="px-5 py-3 font-semibold">PO Number</th>
                <th className="px-5 py-3 font-semibold">Reference</th>
                <th className="px-5 py-3 font-semibold">Subcontractor</th>
                <th className="px-5 py-3 font-semibold">Date</th>
                <th className="px-5 py-3 font-semibold text-right">Subtotal</th>
                <th className="px-5 py-3 font-semibold text-right">Tax</th>
                <th className="px-5 py-3 font-semibold text-right">Total</th>
                <th className="px-5 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="text-sm text-slate-900">
              {loading && (
                <tr><td colSpan="8" className="px-5 py-10 text-center text-slate-500">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan="8" className="px-5 py-12 text-center">
                    <div className="text-slate-700 font-medium">No purchase orders yet</div>
                    <div className="text-slate-500 text-sm mt-1">Create your first PO to get started.</div>
                  </td>
                </tr>
              )}
              {!loading && filtered.map((po) => (
                <tr key={po.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`po-row-${po.id}`}>
                  <td className="px-5 py-3 font-medium">
                    <Link to={`/purchase-orders/${po.id}`} className="hover:underline">{po.number}</Link>
                  </td>
                  <td className="px-5 py-3 text-slate-700">{po.reference || "—"}</td>
                  <td className="px-5 py-3 text-slate-700">{po.customer_snapshot?.name || "—"}</td>
                  <td className="px-5 py-3 text-slate-700">{fmtDate(po.date)}</td>
                  <td className="px-5 py-3 text-right tabular-nums">{gbp(po.subtotal)}</td>
                  <td className="px-5 py-3 text-right tabular-nums text-slate-500">{gbp(po.tax_amount)}</td>
                  <td className="px-5 py-3 text-right tabular-nums font-medium">{gbp(po.total)}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <a
                        data-testid={`po-pdf-${po.id}`}
                        href={fileUrl(`/purchase-orders/${po.id}/pdf`)}
                        download
                        rel="noopener"
                        title="Download PDF"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-sm text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
                      >
                        <Download className="h-4 w-4" />
                      </a>
                      <Link to={`/purchase-orders/${po.id}`}>
                        <Button data-testid={`po-edit-${po.id}`} variant="ghost" size="icon" className="h-8 w-8 rounded-sm" title="View / edit">
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </Link>
                      <Button data-testid={`po-delete-${po.id}`} variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-red-600 hover:text-red-700" onClick={() => onDelete(po)} title="Delete">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
